import ReactMarkdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeSlug from 'rehype-slug';
import { Link } from 'react-router-dom';
import { useState, type ReactNode, isValidElement } from 'react';
import { MediaPlaceholder } from './MediaPlaceholder';
import { Callout } from './Callout';
import { FeatureStatus } from './FeatureStatus';
import { Icon } from '../Icon';
import { copyToClipboard } from '../../lib/publicUrl';

interface DocMarkdownProps {
    content: string;
}

/**
 * Rendu d'une page Markdown de la documentation.
 *
 * Extensions reconnues :
 *   - ```media { json }```           → <MediaPlaceholder>
 *   - ```callout type="tip" ...```   → <Callout>
 *   - ```status stable``` inline      → <FeatureStatus>
 *
 * Les liens `/docs/...` sont intercepts par `<Link>` pour rester dans le SPA.
 */
export function DocMarkdown({ content }: DocMarkdownProps) {
    const components: Components = {
        a: ({ href = '', children, ...rest }) => {
            const isInternal =
                href.startsWith('/docs') ||
                href.startsWith('/app/') ||
                href.startsWith('#');
            if (isInternal && !href.startsWith('#')) {
                return (
                    <Link to={href} {...(rest as object)}>
                        {children}
                    </Link>
                );
            }
            return (
                <a
                    href={href}
                    target={href.startsWith('http') ? '_blank' : undefined}
                    rel={href.startsWith('http') ? 'noopener noreferrer' : undefined}
                    {...rest}
                >
                    {children}
                </a>
            );
        },

        code: ({ className, children, ...rest }) => {
            const raw = normalizeNewlines(childrenToText(children)).replace(/\n$/, '');
            const lang = (className ?? '').replace('language-', '');

            // Bloc spécial ``` media ```
            if (lang === 'media') {
                return renderMediaBlock(raw);
            }
            // Bloc spécial ``` callout ```
            if (lang === 'callout') {
                return renderCalloutBlock(raw);
            }
            // Bloc de code classique → composant avec bouton copier
            if (lang && lang.length > 0) {
                return <CodeBlock code={raw} language={lang} />;
            }
            // Inline code
            return (
                <code className={className} {...rest}>
                    {children}
                </code>
            );
        },

        // On enveloppe les <pre> pour éviter le double wrapping quand code est déjà remplacé
        pre: ({ children }) => <>{children}</>,

        // Tables plus lisibles
        table: ({ children }) => (
            <div className="doc-table-wrap">
                <table>{children}</table>
            </div>
        ),

        h1: () => null, // Le titre principal est déjà rendu au-dessus par DocsPage
    };

    return (
        <div className="doc-prose">
            <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                rehypePlugins={[rehypeSlug]}
                components={components}
            >
                {content}
            </ReactMarkdown>
        </div>
    );
}

/* ================================================================
   Code block avec bouton Copier
   ================================================================ */
function CodeBlock({ code, language }: { code: string; language: string }) {
    const [copied, setCopied] = useState(false);
    const handleCopy = async () => {
        const ok = await copyToClipboard(code);
        if (ok) {
            setCopied(true);
            window.setTimeout(() => setCopied(false), 1500);
        }
    };
    return (
        <div className="doc-code">
            <div className="doc-code-head">
                <span className="doc-code-lang">{language}</span>
                <button
                    type="button"
                    className="doc-code-copy"
                    onClick={handleCopy}
                    aria-label="Copier le code"
                >
                    <Icon name={copied ? 'check' : 'copy'} size={13} />
                    {copied ? 'Copié' : 'Copier'}
                </button>
            </div>
            <pre>
                <code>{code}</code>
            </pre>
        </div>
    );
}

/* ================================================================
   Bloc « media » — placeholder riche
   Format accepté (YAML-like) :
     kind: screenshot
     ratio: 16:9
     title: "Capture de l'éditeur"
     recommended: "…"
     alt1: "…"
     alt2: "…"
     notes: "…"
   ================================================================ */
function renderMediaBlock(raw: string): ReactNode {
    const data = parsePseudoYaml(raw);
    return (
        <MediaPlaceholder
            kind={(data.kind as never) ?? 'screenshot'}
            ratio={data.ratio}
            title={data.title}
            recommended={data.recommended ?? data.main ?? 'À définir'}
            alt1={data.alt1}
            alt2={data.alt2}
            notes={data.notes}
        />
    );
}

function renderCalloutBlock(raw: string): ReactNode {
    const data = parsePseudoYaml(raw);
    const type = (data.type as never) ?? 'info';
    const body = data.body ?? raw;
    return (
        <Callout type={type} title={data.title}>
            <div className="doc-callout-md">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{body}</ReactMarkdown>
            </div>
        </Callout>
    );
}

/**
 * Extrait tout le texte d'un `children` react-markdown, qui peut être :
 *   - une string,
 *   - un nombre,
 *   - un tableau mélangeant strings et `<span>…</span>` (quand un plugin de
 *     syntax highlighting est actif ou simplement à cause du découpage
 *     interne de react-markdown).
 *
 * Un `String(children)` naïf sur un tableau injecte des virgules entre les
 * éléments et casse le parseur — d'où ce helper récursif.
 */
function childrenToText(node: ReactNode): string {
    if (node == null || typeof node === 'boolean') return '';
    if (typeof node === 'string') return node;
    if (typeof node === 'number') return String(node);
    if (Array.isArray(node)) return node.map(childrenToText).join('');
    if (isValidElement(node)) {
        const props = node.props as { children?: ReactNode };
        return childrenToText(props.children);
    }
    return '';
}

function normalizeNewlines(s: string): string {
    // Retire un éventuel BOM et normalise CRLF/CR en LF.
    return s.replace(/^\uFEFF/, '').replace(/\r\n?/g, '\n');
}

/**
 * Mini-parseur YAML / clé-valeur très tolérant.
 * Suffisant pour nos blocs media/callout sans dépendance.
 * Supporte :
 *   clé: valeur courte
 *   clé: "chaîne avec : des choses"
 *   clé: |
 *     paragraphe
 *     multi-ligne
 */
function parsePseudoYaml(raw: string): Record<string, string> {
    const out: Record<string, string> = {};
    const lines = normalizeNewlines(raw).split('\n');
    let i = 0;
    while (i < lines.length) {
        const line = lines[i];
        // Ignorer les lignes vides ou les lignes indentées orphelines
        if (!line.trim()) { i++; continue; }
        const m = /^([a-zA-Z0-9_-]+)\s*:\s*(.*?)\s*$/.exec(line);
        if (!m) { i++; continue; }
        const key = m[1];
        let value = m[2];

        if (value === '|' || value === '>') {
            // Bloc multi-ligne : on prend les lignes indentées suivantes
            const block: string[] = [];
            i++;
            while (i < lines.length) {
                const next = lines[i];
                if (/^[ \t]{2,}/.test(next) || next.trim() === '') {
                    block.push(next.replace(/^[ \t]{1,4}/, ''));
                    i++;
                } else {
                    break;
                }
            }
            out[key] = block.join('\n').trim();
            continue;
        }

        // Chaîne simple — on enlève les guillemets éventuels
        if (
            (value.startsWith('"') && value.endsWith('"')) ||
            (value.startsWith("'") && value.endsWith("'"))
        ) {
            value = value.slice(1, -1);
        }
        out[key] = value;
        i++;
    }
    return out;
}

/* Exposer FeatureStatus pour qu'il soit utilisable dans la page d'accueil des docs. */
export { FeatureStatus };
